import { Button } from '@thanx/uikit/button'
import { Input } from '@thanx/uikit/input'
import { Notification } from '@thanx/uikit/notification'
import { Text } from '@thanx/uikit/text'
import {
  useGetCannedResponsesQuery,
  useGetRecoveryConfigQuery,
} from 'api/recoveryAi'
import type { RecoveryConfig } from 'api/recoveryAi.types'
import Spinner from 'components/Spinner'
import { buildTranslate } from 'locales'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { push } from 'react-router-redux'
import {
  AGENT_NAME_MAX_LENGTH,
  composeAgentIntro,
  normalizeAgentName,
  stripGreetingDisclosure,
  validateAgentName,
} from 'scenes/RecoveryAI/components/agentIntro'
import { longTextWrap } from 'scenes/RecoveryAI/components/longTextWrap'
import SettingsPaneChrome from 'scenes/RecoveryAI/components/SettingsPaneChrome'
import { successNotificationOverrides } from 'scenes/RecoveryAI/components/successNotificationOverrides'
import { BRAND_VOICE_SETTINGS_PATH } from 'scenes/RecoveryAI/routes'
import useMerchantBrand from 'scenes/RecoveryAI/Sandbox/useMerchantBrand'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import { useAbility } from 'utilities/ability'
import { isPlainObject } from 'utilities/isPlainObject'
import { useSaveAndTest } from './useSaveAndTest'
import { useUnsavedChangesPrompt } from './useUnsavedChangesPrompt'

const t = buildTranslate('recovery_ai.agent_introduction')

const PREVIEW_WIDTH = '300px'
const SIDE_BY_SIDE_BREAKPOINT = 768
const COLUMN_GAP = '48px'
const NAME_INPUT_ID = 'agent-introduction-name'
const NAME_ERROR_ID = 'agent-introduction-name-error'
const HTTP_BAD_REQUEST = 400

const getRejection = (error: unknown): string | null => {
  if (!isPlainObject(error) || error.status !== HTTP_BAD_REQUEST) return null
  const data = isPlainObject(error.data) ? error.data : {}
  const errors = isPlainObject(data.errors) ? data.errors : {}
  const messages = Array.isArray(errors.messages)
    ? errors.messages.filter((m): m is string => typeof m === 'string')
    : []
  return messages.join(' ') || ''
}

const Label = ({ children }: { children: React.ReactNode }) => (
  <Text.BodyText3 bold color="grey90">
    {children}
  </Text.BodyText3>
)

const AgentIntroductionPane = () => {
  const [css] = useStyletron()
  const dispatch = useDispatch()
  const ability = useAbility()
  const canManage = ability.can('manage', 'MerchantSettings')
  const {
    data: config,
    isLoading,
    isError: isFetchError,
  } = useGetRecoveryConfigQuery()
  const { data: cannedResponses } = useGetCannedResponsesQuery()
  const { name: merchantName } = useMerchantBrand()

  const [draft, setDraft] = useState(config?.agent_name ?? '')
  const [seed, setSeed] = useState(config?.agent_name ?? '')
  const trimmedDraft = normalizeAgentName(draft)

  const incomingName = config ? config.agent_name ?? '' : seed
  if (incomingName !== seed) {
    const normalizedDraft = normalizeAgentName(draft)
    if (
      normalizedDraft === normalizeAgentName(seed) ||
      normalizedDraft === normalizeAgentName(incomingName)
    ) {
      setDraft(incomingName)
    }
    setSeed(incomingName)
  }

  const submittedRef = useRef('')
  const buildPayload = useCallback((): Partial<RecoveryConfig> => {
    submittedRef.current = trimmedDraft
    return { agent_name: trimmedDraft }
  }, [trimmedDraft])

  const isDirty = useMemo(() => {
    if (!config) return false
    return trimmedDraft !== normalizeAgentName(config.agent_name)
  }, [config, trimmedDraft])

  const {
    handleSave,
    clearSuccess,
    saveSuccess,
    isSaving,
    isSaveError,
    saveError,
    canSave,
    guardBypassRef,
  } = useSaveAndTest(buildPayload, isDirty)

  const rejection = getRejection(saveError)
  const [rejectedDraft, setRejectedDraft] = useState<string | null>(null)
  useEffect(() => {
    setRejectedDraft(rejection === null ? null : submittedRef.current)
  }, [rejection, saveError])

  const unsavedChangesPrompt = useUnsavedChangesPrompt(isDirty, guardBypassRef)

  const handleChange = useCallback(
    (next: string) => {
      setDraft(next)
      setRejectedDraft(null)
      clearSuccess()
    },
    [clearSuccess]
  )

  const handleSwitchToDefault = useCallback(() => {
    setDraft('')
    setRejectedDraft(null)
    clearSuccess()
  }, [clearSuccess])

  const handleOpenBrandVoice = useCallback(
    () => dispatch(push(BRAND_VOICE_SETTINGS_PATH)),
    [dispatch]
  )

  const restOfMessage = useMemo(() => {
    const resolved = cannedResponses?.find(
      r => r.responseType === 'greeting'
    )?.body
    const body = resolved ? stripGreetingDisclosure(resolved).trim() : ''
    return body || t('rest_fallback')
  }, [cannedResponses])

  if (isLoading) {
    return (
      <Spinner isLoading size="4x" className="text-center margin-top-huge" />
    )
  }

  if (isFetchError || !config) {
    return (
      <div className="p-l">
        <Notification kind="negative" size="full">
          {t('fetch_error')}
        </Notification>
      </div>
    )
  }

  const isDefault = trimmedDraft === ''
  const validationError = validateAgentName(draft)
  const serverRejected =
    rejection !== null && rejectedDraft !== null && !validationError
  const fieldError = validationError
    ? t(`name_error_${validationError}`, { max: AGENT_NAME_MAX_LENGTH })
    : serverRejected
    ? rejection || t('name_error_rejected')
    : null
  const intro = composeAgentIntro(trimmedDraft, merchantName)

  return (
    <SettingsPaneChrome title={t('title')} subtitle={t('description')}>
      {unsavedChangesPrompt}

      {isSaveError && rejection === null && (
        <div className="mb-m">
          <Notification kind="negative" size="full">
            {t('save_error')}
          </Notification>
        </div>
      )}

      {saveSuccess && !isSaveError && (
        <div className="mb-m">
          <Notification
            kind="positive"
            size="full"
            overrides={successNotificationOverrides}
          >
            {t('save_success')}
          </Notification>
        </div>
      )}

      <div
        className={`d-flex ${css({
          flexDirection: 'column',
          rowGap: COLUMN_GAP,
          alignItems: 'stretch',
          [`@media (min-width: ${SIDE_BY_SIDE_BREAKPOINT}px)`]: {
            flexDirection: 'row',
            columnGap: COLUMN_GAP,
            rowGap: 0,
            alignItems: 'flex-start',
          },
        })}`}
      >
        <div
          className={css({
            flexGrow: 1,
            flexShrink: 1,
            flexBasis: '0',
            minWidth: 0,
          })}
        >
          <div
            className={`d-flex justify-content-between mb-xs ${css({
              alignItems: 'baseline',
            })}`}
          >
            <label htmlFor={NAME_INPUT_ID}>
              <Label>{t('name_label')}</Label>
            </label>
            {isDefault ? (
              <Text.BodyText4 color="grey70">
                {t('thanx_default')}
              </Text.BodyText4>
            ) : (
              <button
                type="button"
                onClick={handleSwitchToDefault}
                disabled={!canManage}
                className={css({
                  borderTopWidth: 0,
                  borderRightWidth: 0,
                  borderBottomWidth: 0,
                  borderLeftWidth: 0,
                  backgroundColor: 'transparent',
                  paddingTop: 0,
                  paddingRight: 0,
                  paddingBottom: 0,
                  paddingLeft: 0,
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: '14px',
                  lineHeight: '20px',
                  color: merchantTheme.colors.belizeHole50,
                  ':hover': { textDecoration: 'underline' },
                })}
              >
                {t('switch_to_default')}
              </button>
            )}
          </div>

          <Input
            id={NAME_INPUT_ID}
            value={draft}
            onChange={handleChange}
            placeholder={t('name_placeholder')}
            disabled={!canManage}
            error={Boolean(fieldError)}
            aria-describedby={fieldError ? NAME_ERROR_ID : undefined}
          />
          <div id={NAME_ERROR_ID} aria-live="polite">
            {fieldError && (
              <Text.BodyText4 color="cayenne50" tag="p" className="mt-xs">
                {fieldError}
              </Text.BodyText4>
            )}
          </div>

          <div className="mt-xl">
            <Label>{t('rest_label')}</Label>
            <div
              className={`mt-xs ${css({
                borderTopWidth: '1px',
                borderRightWidth: '1px',
                borderBottomWidth: '1px',
                borderLeftWidth: '1px',
                borderTopStyle: 'solid',
                borderRightStyle: 'solid',
                borderBottomStyle: 'solid',
                borderLeftStyle: 'solid',
                borderTopColor: merchantTheme.colors.grey30,
                borderRightColor: merchantTheme.colors.grey30,
                borderBottomColor: merchantTheme.colors.grey30,
                borderLeftColor: merchantTheme.colors.grey30,
                borderTopLeftRadius: '4px',
                borderTopRightRadius: '4px',
                borderBottomLeftRadius: '4px',
                borderBottomRightRadius: '4px',
                backgroundColor: merchantTheme.colors.grey05,
                paddingTop: '12px',
                paddingRight: '16px',
                paddingBottom: '12px',
                paddingLeft: '16px',
                whiteSpace: 'pre-wrap',
                ...longTextWrap,
              })}`}
              data-testid="agent-introduction-rest"
            >
              <Text.BodyText3 color="grey70">{restOfMessage}</Text.BodyText3>
            </div>
            <Text.BodyText4 color="grey70" tag="p" className="mt-xs">
              {t('rest_helper')}{' '}
              <button
                type="button"
                onClick={handleOpenBrandVoice}
                className={css({
                  borderTopWidth: 0,
                  borderRightWidth: 0,
                  borderBottomWidth: 0,
                  borderLeftWidth: 0,
                  backgroundColor: 'transparent',
                  paddingTop: 0,
                  paddingRight: 0,
                  paddingBottom: 0,
                  paddingLeft: 0,
                  cursor: 'pointer',
                  fontSize: 'inherit',
                  lineHeight: 'inherit',
                  color: merchantTheme.colors.belizeHole50,
                  ':hover': { textDecoration: 'underline' },
                })}
              >
                {t('rest_helper_link')}
              </button>
              .
            </Text.BodyText4>
          </div>

          <div
            className={`d-flex mt-xl ${css({
              columnGap: '16px',
              alignItems: 'center',
            })}`}
          >
            <Button
              kind="primary"
              onClick={handleSave}
              disabled={!canManage || !canSave || Boolean(validationError)}
              isLoading={isSaving}
            >
              {t('save')}
            </Button>
            {isDirty && (
              <Text.BodyText4 color="grey70">
                {t('unsaved_changes')}
              </Text.BodyText4>
            )}
          </div>
        </div>

        <div
          className={css({
            width: '100%',
            maxWidth: PREVIEW_WIDTH,
            flexShrink: 0,
            [`@media (min-width: ${SIDE_BY_SIDE_BREAKPOINT}px)`]: {
              width: PREVIEW_WIDTH,
            },
          })}
        >
          <Text.BodyText5
            bold
            color="grey70"
            tag="p"
            className={`mb-s ${css({
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            })}`}
          >
            {t('preview_eyebrow')}
          </Text.BodyText5>
          <div
            role="group"
            aria-label={t('preview_aria_label')}
            className={css({
              backgroundColor: merchantTheme.colors.white,
              borderTopLeftRadius: '20px',
              borderTopRightRadius: '20px',
              borderBottomLeftRadius: '20px',
              borderBottomRightRadius: '20px',
              borderTopWidth: '10px',
              borderRightWidth: '10px',
              borderBottomWidth: '10px',
              borderLeftWidth: '10px',
              borderTopStyle: 'solid',
              borderRightStyle: 'solid',
              borderBottomStyle: 'solid',
              borderLeftStyle: 'solid',
              borderTopColor: merchantTheme.colors.grey90,
              borderRightColor: merchantTheme.colors.grey90,
              borderBottomColor: merchantTheme.colors.grey90,
              borderLeftColor: merchantTheme.colors.grey90,
              paddingTop: '16px',
              paddingRight: '12px',
              paddingBottom: '16px',
              paddingLeft: '12px',
            })}
          >
            {merchantName && (
              <Text.BodyText5 color="grey70" tag="p" className="mb-xs">
                {merchantName}
              </Text.BodyText5>
            )}
            <div
              data-testid="agent-introduction-preview"
              className={css({
                backgroundColor: merchantTheme.colors.grey10,
                borderTopLeftRadius: '4px',
                borderTopRightRadius: '14px',
                borderBottomLeftRadius: '14px',
                borderBottomRightRadius: '14px',
                paddingTop: '9px',
                paddingRight: '12px',
                paddingBottom: '9px',
                paddingLeft: '12px',
                whiteSpace: 'pre-line',
                ...longTextWrap,
              })}
            >
              <Text.BodyText4 color="grey90">
                <span data-testid="agent-introduction-preview-intro">
                  {intro}
                </span>
                {'\n\n'}
                {restOfMessage}
              </Text.BodyText4>
            </div>
          </div>
        </div>
      </div>
    </SettingsPaneChrome>
  )
}

export default AgentIntroductionPane
